/* iter-admin-themes — Selectable admin panel color presets.
 *
 * Four presets favoured by DevTools/IDEs, chosen for eye comfort and
 * strong figure-ground contrast so the *currently viewed* panel is
 * always distinct from the sidebar chrome:
 *
 *   1. Parchment  — warm cream + copper ink (default; matches app)
 *   2. Slate      — cool neutral gray + amber accent (Adobe-adjacent)
 *   3. Solarized  — Ethan Schoonover's low-contrast dark
 *   4. Nord       — Arctic blue-gray dark (very popular editor theme)
 *
 * The user's pick is persisted in localStorage keyed per-admin and
 * applied via `data-admin-theme` on the admin root — theme rules live
 * in `index.css` so nothing here duplicates design tokens.
 */
import React, { useEffect, useState } from 'react';
import { Palette } from 'lucide-react';
import { adminAuth } from '@/lib/api';

export const ADMIN_THEMES = [
  { id: 'windows',      label: 'Windows',      tone: 'Fluent light · Segoe UI',
    preview: { bg: '#f3f3f3', card: '#ffffff', ink: '#202020', accent: '#0078d4' } },
  { id: 'windows-dark', label: 'Windows Dark', tone: 'Fluent dark · late-night reviews',
    preview: { bg: '#1f1f1f', card: '#2b2b2b', ink: '#f3f3f3', accent: '#4cc2ff' } },
  { id: 'parchment',    label: 'Parchment',    tone: 'Warm cream · copper ink',
    preview: { bg: '#f4eae0', card: '#fbf6f0', ink: '#2a2320', accent: '#c96b28' } },
  { id: 'slate',        label: 'Slate',        tone: 'Cool neutral · amber',
    preview: { bg: '#e6e8ec', card: '#ffffff', ink: '#20232a', accent: '#d4880c' } },
  { id: 'solarized',    label: 'Solarized',    tone: 'Low-contrast dark',
    preview: { bg: '#002b36', card: '#073642', ink: '#eee8d5', accent: '#b58900' } },
  { id: 'nord',         label: 'Nord',         tone: 'Arctic blue-gray dark',
    preview: { bg: '#2e3440', card: '#3b4252', ink: '#d8dee9', accent: '#88c0d0' } },
];

const storageKey = (user) => `cnr:admin-theme:${user || '_'}`;

export function readAdminTheme() {
  try { return window.localStorage.getItem(storageKey(adminAuth?.user)) || 'windows'; }
  catch { return 'windows'; }
}

/**
 * Applies the given theme to the document by setting
 * `data-admin-theme` on <html>. Kept idempotent so calling it in a
 * useEffect on every render is safe.
 */
export function applyAdminTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-admin-theme', theme || 'windows');
}

export default function AdminThemeSwitcher() {
  const [theme, setTheme] = useState(readAdminTheme());
  const [open, setOpen] = useState(false);

  useEffect(() => { applyAdminTheme(theme); }, [theme]);

  // Re-apply when the switcher unmounts is unnecessary (the html
  // attr persists), but clear on admin logout so the bookstore or
  // marketing surfaces don't inherit a dark palette.
  useEffect(() => () => { /* keep theme on unmount */ }, []);

  const pick = (id) => {
    setTheme(id);
    try { window.localStorage.setItem(storageKey(adminAuth?.user), id); } catch { /* private */ }
    setOpen(false);
  };

  const active = ADMIN_THEMES.find(t => t.id === theme) || ADMIN_THEMES[0];
  // iter-theme-mini-preview — miniature "sidebar + panel" chip so
  // admins can eyeball the palette without switching to it. 48×28
  // pixels; renders inline beside each option in the dropdown.
  const MiniPreview = ({ p }) => (
    <span
      className="inline-flex h-7 w-12 shrink-0 overflow-hidden rounded-sm border border-border shadow-sm"
      aria-hidden="true"
    >
      <span className="flex h-full w-4 flex-col items-center justify-between py-0.5" style={{ backgroundColor: p.bg }}>
        <span className="h-0.5 w-2 rounded-full" style={{ backgroundColor: p.ink, opacity: 0.5 }} />
        <span className="h-0.5 w-2 rounded-full" style={{ backgroundColor: p.ink, opacity: 0.4 }} />
        <span className="h-0.5 w-2 rounded-full" style={{ backgroundColor: p.accent }} />
      </span>
      <span className="flex h-full flex-1 flex-col items-start justify-center gap-0.5 px-1" style={{ backgroundColor: p.card }}>
        <span className="h-0.5 w-6" style={{ backgroundColor: p.ink, opacity: 0.7 }} />
        <span className="h-0.5 w-4" style={{ backgroundColor: p.ink, opacity: 0.5 }} />
        <span className="h-1 w-3 rounded-sm" style={{ backgroundColor: p.accent }} />
      </span>
    </span>
  );

  return (
    <div className="relative px-1" data-testid="admin-theme-switcher">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex w-full items-center justify-between gap-2 rounded-sm border border-border bg-panel-header/40 px-2 py-1.5 text-[11px] hover:bg-panel-header/70"
        data-testid="admin-theme-switcher-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          <Palette className="h-3 w-3" /> Theme
        </span>
        <span className="flex items-center gap-1.5">
          <MiniPreview p={active.preview} />
          <span className="text-muted-foreground">{active.label}</span>
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-1 right-1 top-full z-50 mt-1 rounded-md border border-border bg-popover p-1 shadow-lg"
          data-testid="admin-theme-switcher-menu"
        >
          {ADMIN_THEMES.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => pick(t.id)}
              className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[11px] hover:bg-panel-header/60 ${t.id === theme ? 'bg-panel-header/40' : ''}`}
              data-testid={`admin-theme-option-${t.id}`}
            >
              <MiniPreview p={t.preview} />
              <span className="flex-1 truncate">
                <span className="font-semibold">{t.label}</span>
                <span className="ml-1 text-muted-foreground">— {t.tone}</span>
              </span>
              {t.id === theme && <span className="text-primary">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
